import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import {
  Box,
  Button,
  List,
  type MediumIcon,
  Row,
  Title,
  Text,
  useToggle,
  Tooltip,
} from '@factorialco/deprecated-design-system'
import { Office, Briefcase } from '@factorialco/f0-react/icons/app'
import { F0Icon } from '@factorialco/f0-react'
import { Switch } from '@factorialco/f0-react/dist/experimental'
import {
  HideCompensationsCycleDocument,
  SettingCyclesDocument,
  UnhideCompensationsCycleDocument,
} from 'generated/resources/graphql'
import type { SettingCyclesQuery } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import HideCycleConfirmationModal from './HideCycleConfirmationModal'

type Cycle = SettingCyclesQuery['compensations']['cyclesConnection']['nodes'][number]

export interface ModuleConfig {
  key: string
  titleKey: string
  illustration: MediumIcon
  navigationPath: string
}

const Cycles = () => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const featureIds = ['factorial_professionals', 'dev_bookkeepers_management_cycles']
  const { data, loading, error } = useQuery(SettingCyclesDocument, {
    variables: {
      companyId,
      featureIds,
    },
  })
  const {
    has_factorial_professionals: hasProfessionals,
    has_dev_bookkeepers_management_cycles: hasDevBookkeepersManagementCycles,
  } = checkFeatures(featureIds, data?.features?.featuresConnection.nodes)

  const cycles = data?.compensations?.cyclesConnection?.nodes ?? []

  const [pendingCycle, setPendingCycle] = useState<{
    id: string
    label: string
    currency: string
    isHiding: boolean
  } | null>(null)

  const [hideCycle] = useMutation(HideCompensationsCycleDocument, {
    extractorFn: (payload) =>
      payload.compensationsMutations?.hideCompensationsCycle ?? { errors: [] },
    refetchQueries: [SettingCyclesDocument],
  })

  const [unhideCycle] = useMutation(UnhideCompensationsCycleDocument, {
    extractorFn: (payload) =>
      payload.compensationsMutations?.unhideCompensationsCycle ?? { errors: [] },
    refetchQueries: [SettingCyclesDocument],
  })

  const handleToggle = (
    cycleId: string,
    cycleLabel: string,
    cycleCurrency: string,
    isCurrentlyVisible: boolean
  ) => {
    const newPendingCycle = {
      id: cycleId,
      label: cycleLabel,
      currency: cycleCurrency,
      isHiding: isCurrentlyVisible,
    }
    setPendingCycle(newPendingCycle)
  }

  const handleConfirm = async () => {
    if (!pendingCycle) return

    if (pendingCycle.isHiding) {
      const result = await hideCycle({ variables: { id: pendingCycle.id } })
      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.save_error'),
        })
      }
    } else {
      const result = await unhideCycle({ variables: { id: pendingCycle.id } })
      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.save_error'),
        })
      }
    }

    setPendingCycle(null)
  }

  const handleCloseModal = () => {
    setPendingCycle(null)
  }

  if (loading) {
    return <Loading />
  }

  if (error) {
    throw new Error('Error fetching data')
  }

  return (
    <Box flexDirection="row" alignItems="center" paddingX="s64" paddingY="s32" width="full">
      <Row>
        <Row.Col halfWidth>
          <Box paddingRight="s64">
            <Title
              variant="500"
              title={i18n.t('compensations.settings.cycles.title')}
              description={i18n.t('compensations.settings.cycles.description')}
              illustration={'calendar-clock'}
            />
            {hasProfessionals && hasDevBookkeepersManagementCycles && (
              <Text.Normal>
                {i18n.tView('compensations.settings.cycles.professionals_description', {
                  professionals_label: (
                    <Text.Normal inline weight="bold">
                      {i18n.t('compensations.settings.cycles.professionals_label')}
                    </Text.Normal>
                  ),
                })}
              </Text.Normal>
            )}
          </Box>
        </Row.Col>
        <Row.Col halfWidth>
          <List>
            {cycles.map((cycle) => {
              return (
                <CycleListItem
                  key={cycle.id}
                  cycle={cycle}
                  hasProfessionals={hasProfessionals ?? false}
                  handleToggle={handleToggle}
                  hasDevBookkeepersManagementCycles={hasDevBookkeepersManagementCycles ?? false}
                />
              )
            })}
          </List>
        </Row.Col>
      </Row>
      {pendingCycle && (
        <HideCycleConfirmationModal
          isHiding={pendingCycle.isHiding}
          cycleLabel={pendingCycle.label}
          cycleCurrency={pendingCycle.currency}
          onConfirm={handleConfirm}
          onClose={handleCloseModal}
        />
      )}
    </Box>
  )
}

interface CycleListItemProps {
  cycle: Cycle
  hasProfessionals: boolean
  hasDevBookkeepersManagementCycles: boolean
  handleToggle: (
    cycleId: string,
    cycleLabel: string,
    cycleCurrency: string,
    isCurrentlyVisible: boolean
  ) => void
}

const CycleListItem = ({
  cycle,
  hasProfessionals,
  hasDevBookkeepersManagementCycles,
  handleToggle,
}: CycleListItemProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [showToolTip, setShowToolTip] = useToggle(false)
  const bookkeepersCount = cycle?.bookkeepersConnection?.totalCount ?? 0
  const isVisible = !cycle.hiddenAt
  const legalEntityLabels = cycle?.legalEntitiesLabel?.split(',').map((label) => label.trim()) ?? []

  let firstLegalEntitiesLabel = ''
  let restLegalEntitiesLabel = ''
  if (legalEntityLabels.length > 3) {
    const firstLabels = legalEntityLabels.slice(0, 3)
    firstLegalEntitiesLabel = firstLabels.join(', ') + ' +' + (legalEntityLabels.length - 3)
    restLegalEntitiesLabel = legalEntityLabels.slice(3).join(', ')
  } else {
    firstLegalEntitiesLabel = cycle.legalEntitiesLabel ?? ''
    restLegalEntitiesLabel = ''
  }
  const canShowTooltip = (cycle?.legalEntitiesLabel?.split(',')?.length ?? 0) > 3

  return (
    <List.Item key={cycle.id}>
      <Box flexDirection="column">
        <List.Title>{cycle.countryLabel}</List.Title>
        <List.Description>
          <Box flexDirection="row" gap="s4" alignItems="center" overflowX="hidden" maxWidth="s600">
            <F0Icon icon={Office} size="lg" />
            {canShowTooltip ? (
              <Tooltip.Dialog
                open={showToolTip}
                toggle={setShowToolTip}
                attachment="top center"
                label={restLegalEntitiesLabel}
              >
                <Text.Normal ellipsis overflowWrap>
                  {firstLegalEntitiesLabel}
                </Text.Normal>
              </Tooltip.Dialog>
            ) : (
              <Text.Normal>{firstLegalEntitiesLabel}</Text.Normal>
            )}
          </Box>
          {hasProfessionals && hasDevBookkeepersManagementCycles && (
            <Box flexDirection="row" gap="s4" alignItems="center">
              <F0Icon icon={Briefcase} size="lg" />
              <Text.Normal>
                {bookkeepersCount === 0
                  ? i18n.t('compensations.settings.cycles.bookkeepers_count.zero')
                  : i18n.tp('compensations.settings.cycles.bookkeepers_count', {
                      count: bookkeepersCount,
                    })}
              </Text.Normal>
            </Box>
          )}
        </List.Description>
      </Box>
      <List.ActionItem>
        <Box flexDirection="row" alignItems="center" gap="s16">
          <Switch
            checked={isVisible}
            onCheckedChange={() =>
              handleToggle(cycle.id, cycle.countryLabel || '', cycle.currency || '', isVisible)
            }
          />
          <Button
            type="secondary"
            iconName="arrow-right"
            disabled={!hasProfessionals || !hasDevBookkeepersManagementCycles}
            onClick={() => {
              if (hasProfessionals && hasDevBookkeepersManagementCycles) {
                navigate(navigation.settings.compensations.cycles.id.fullPath({ id: cycle.id }))
              }
            }}
          ></Button>
        </Box>
      </List.ActionItem>
    </List.Item>
  )
}

export default Cycles
